import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  useToast,
} from '@eluve/components';
import { SUPPORTED_UI_LANGUAGES } from '@eluve/i18n';

import {
  getTenantDefaultUiLanguageQuery,
  setTenantDefaultUiLanguageMutation,
} from './operations';

interface InterfaceLanguageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  currentUiLanguage: string;
}

export const InterfaceLanguageDialog: React.FC<
  InterfaceLanguageDialogProps
> = ({ open, onOpenChange, tenantId, currentUiLanguage }) => {
  const toast = useToast();
  const [pendingValue, setPendingValue] = useState(currentUiLanguage);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPendingValue(currentUiLanguage);
    }
  }, [open, currentUiLanguage]);

  const { data: countData, loading: countLoading } = useQuery(
    getTenantDefaultUiLanguageQuery,
    {
      variables: { tenantId },
      skip: !open,
      fetchPolicy: 'network-only',
    },
  );

  const affectedUserCount = Number(
    countData?.defaultTenantUserSettingsByPk?.nullUiLanguageUserCount ?? 0,
  );

  const [setTenantDefaultUiLanguage] = useMutation(
    setTenantDefaultUiLanguageMutation,
    {
      onError: () => {
        toast.error('Failed to update interface language');
      },
    },
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await setTenantDefaultUiLanguage({
        variables: { tenantId, uiLanguage: pendingValue },
      });
      if (result.data) {
        onOpenChange(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled =
    pendingValue === currentUiLanguage || countLoading || isSaving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Change Interface Language</DialogTitle>
          <DialogDescription>
            This will change the UI for{' '}
            {countLoading ? (
              'some users'
            ) : (
              <>
                {affectedUserCount} {affectedUserCount === 1 ? 'user' : 'users'}
              </>
            )}{' '}
            in this organization, since users who have explicitly chosen a
            language are not affected.
          </DialogDescription>
        </DialogHeader>
        <Stack gap={2}>
          <Select value={pendingValue} onValueChange={setPendingValue}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_UI_LANGUAGES.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.nativeLabel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" disabled={isSaving}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
